import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageChat from "../components/PageChat";
import {
    ArrowLeft, Crosshair, Coins, Lock, Loader2, Trophy, XCircle, Users, Swords,
} from "lucide-react";

const ANTES = [1, 5, 10, 25];

function wsUrl(token, ante) {
    const base = API.replace(/^http/, "ws");
    return `${base}/duel/ws?token=${encodeURIComponent(token)}&ante=${ante}`;
}

export default function Duel() {
    const { user, loading: authLoading, refresh } = useAuth();
    const [credits, setCredits] = useState(null);
    const [ante, setAnte] = useState(1);
    const [phase, setPhase] = useState("idle"); // idle | queued | playing | ended
    const [match, setMatch] = useState(null);
    const [state, setState] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [queueSizes, setQueueSizes] = useState({});

    const wsRef = useRef(null);
    const canvasRef = useRef(null);
    const keysRef = useRef({ w: false, a: false, s: false, d: false });
    const aimRef = useRef({ x: 1, y: 0 });
    const shootRef = useRef(false);
    const lastSentRef = useRef(0);
    const stateRef = useRef(null);
    const matchRef = useRef(null);

    const isLoggedIn = user && user !== false;

    const loadBalance = useCallback(async () => {
        if (!isLoggedIn) return;
        try { const { data } = await api.get("/payments/i2v/balance"); setCredits(data.credits); } catch {}
    }, [isLoggedIn]);

    const loadQueues = useCallback(async () => {
        try { const { data } = await api.get("/duel/queues"); setQueueSizes(data.queue_sizes || {}); } catch {}
    }, []);

    useEffect(() => { loadBalance(); loadQueues(); }, [loadBalance, loadQueues]);
    useEffect(() => {
        const t = setInterval(loadQueues, 4000);
        return () => clearInterval(t);
    }, [loadQueues]);

    // Render canvas
    useEffect(() => {
        let raf;
        const draw = () => {
            const c = canvasRef.current;
            if (!c) { raf = requestAnimationFrame(draw); return; }
            const m = matchRef.current;
            const s = stateRef.current;
            const ctx = c.getContext("2d");
            const W = m?.arena?.w || 900;
            const H = m?.arena?.h || 560;
            const pr = m?.arena?.player_r || 18;
            const br = m?.arena?.bullet_r || 5;
            c.width = W; c.height = H;
            // background
            ctx.fillStyle = "#FFF9E6"; ctx.fillRect(0, 0, W, H);
            // grid
            ctx.strokeStyle = "rgba(10,10,10,0.06)"; ctx.lineWidth = 1;
            for (let x = 0; x <= W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
            for (let y = 0; y <= H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
            // border
            ctx.strokeStyle = "#0A0A0A"; ctx.lineWidth = 6; ctx.strokeRect(3, 3, W - 6, H - 6);
            if (!s || !m) { raf = requestAnimationFrame(draw); return; }
            // bullets
            for (const b of s.bullets || []) {
                ctx.fillStyle = b.owner_id === m.you.id ? "#FF4500" : "#2563EB";
                ctx.beginPath(); ctx.arc(b.x, b.y, br, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = "#0A0A0A"; ctx.lineWidth = 2; ctx.stroke();
            }
            // players
            for (const p of s.players || []) {
                const isMe = p.id === m.you.id;
                ctx.fillStyle = isMe ? "#FF4500" : "#2563EB";
                ctx.beginPath(); ctx.arc(p.x, p.y, pr, 0, Math.PI * 2); ctx.fill();
                ctx.strokeStyle = "#0A0A0A"; ctx.lineWidth = 4; ctx.stroke();
                // aim line
                const an = Math.hypot(p.aim_x, p.aim_y) || 1;
                ctx.strokeStyle = isMe ? "#FFD700" : "#FFFFFF"; ctx.lineWidth = 3;
                ctx.beginPath();
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(p.x + (p.aim_x / an) * (pr + 16), p.y + (p.aim_y / an) * (pr + 16));
                ctx.stroke();
                // name + hits
                ctx.fillStyle = "#0A0A0A"; ctx.font = "bold 13px Unbounded, sans-serif"; ctx.textAlign = "center";
                ctx.fillText(`@${p.username}`, p.x, p.y - pr - 8);
                ctx.font = "900 14px Unbounded, sans-serif";
                ctx.fillText(`${p.hits}`, p.x, p.y + pr + 18);
            }
            raf = requestAnimationFrame(draw);
        };
        raf = requestAnimationFrame(draw);
        return () => cancelAnimationFrame(raf);
    }, []);

    // Input handlers
    useEffect(() => {
        const dn = (e) => {
            const k = e.key.toLowerCase();
            if (k in keysRef.current) keysRef.current[k] = true;
        };
        const up = (e) => {
            const k = e.key.toLowerCase();
            if (k in keysRef.current) keysRef.current[k] = false;
        };
        window.addEventListener("keydown", dn);
        window.addEventListener("keyup", up);
        return () => { window.removeEventListener("keydown", dn); window.removeEventListener("keyup", up); };
    }, []);

    useEffect(() => {
        const c = canvasRef.current;
        if (!c) return;
        const mm = (e) => {
            const m = matchRef.current; const s = stateRef.current;
            if (!m || !s) return;
            const rect = c.getBoundingClientRect();
            const sx = c.width / rect.width;
            const sy = c.height / rect.height;
            const mx = (e.clientX - rect.left) * sx;
            const my = (e.clientY - rect.top) * sy;
            const me = (s.players || []).find((p) => p.id === m.you.id);
            if (!me) return;
            const dx = mx - me.x; const dy = my - me.y;
            const len = Math.hypot(dx, dy) || 1;
            aimRef.current = { x: dx / len, y: dy / len };
        };
        const md = (e) => { if (e.button === 0) shootRef.current = true; };
        const mu = (e) => { if (e.button === 0) shootRef.current = false; };
        c.addEventListener("mousemove", mm);
        c.addEventListener("mousedown", md);
        c.addEventListener("mouseup", mu);
        c.addEventListener("contextmenu", (e) => e.preventDefault());
        return () => {
            c.removeEventListener("mousemove", mm);
            c.removeEventListener("mousedown", md);
            c.removeEventListener("mouseup", mu);
        };
    }, [phase]);

    // Send input at 25 Hz
    useEffect(() => {
        if (phase !== "playing") return;
        const t = setInterval(() => {
            const ws = wsRef.current;
            if (!ws || ws.readyState !== 1) return;
            const now = performance.now();
            if (now - lastSentRef.current < 40) return;
            lastSentRef.current = now;
            ws.send(JSON.stringify({
                type: "input",
                keys: { ...keysRef.current },
                aim_x: aimRef.current.x,
                aim_y: aimRef.current.y,
                shoot: shootRef.current,
            }));
        }, 40);
        return () => clearInterval(t);
    }, [phase]);

    const findMatch = () => {
        setError("");
        if (!isLoggedIn) return;
        const token = localStorage.getItem("vp_token");
        if (!token) { setError("Please log out and back in"); return; }
        if ((credits ?? 0) < ante) { setError(`Need ${ante} credits to play`); return; }
        const ws = new WebSocket(wsUrl(token, ante));
        wsRef.current = ws;
        setResult(null);
        setMatch(null);
        setState(null);
        matchRef.current = null;
        stateRef.current = null;
        setPhase("queued");
        ws.onmessage = (ev) => {
            const msg = JSON.parse(ev.data);
            if (msg.type === "queued") {
                loadBalance();
            } else if (msg.type === "match_found") {
                setMatch(msg); matchRef.current = msg;
                setPhase("playing");
            } else if (msg.type === "state") {
                setState(msg); stateRef.current = msg;
            } else if (msg.type === "match_end") {
                setResult(msg);
                setPhase("ended");
                loadBalance();
            } else if (msg.type === "error") {
                setError(msg.message || "Server error");
                setPhase("idle");
            }
        };
        ws.onclose = () => {
            wsRef.current = null;
            setPhase((p) => (p === "playing" || p === "queued" ? "ended" : p));
            loadBalance();
        };
        ws.onerror = () => setError("Connection failed");
    };

    const cancelQueue = () => {
        const ws = wsRef.current; if (ws) try { ws.close(); } catch {}
        setPhase("idle"); setMatch(null); setState(null);
        loadBalance();
    };

    const timeLeft = state?.time_left ?? (match ? Math.max(0, match.ends_at - Date.now() / 1000) : 0);
    const myHits = state?.players?.find((p) => p.id === match?.you?.id)?.hits ?? 0;
    const oppHits = state?.players?.find((p) => p.id !== match?.you?.id)?.hits ?? 0;

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <Link to="/" data-testid="duel-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to Endfb
            </Link>

            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-vibrantpink border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg flex-shrink-0">
                    <Swords className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Duel <span className="text-vibrantorange">arena</span>
                    </h1>
                    <p className="font-body text-ink/70 mt-1">
                        Top-down 1v1. 90 seconds. Most hits wins the pot.
                    </p>
                </div>
                {isLoggedIn && credits != null && (
                    <div className="hidden sm:flex items-center gap-1.5 bg-vibrantyellow border-[3px] border-ink rounded-xl px-3 py-2 shadow-brutal flex-shrink-0">
                        <Coins className="w-4 h-4 text-ink" strokeWidth={2.5} />
                        <span className="font-heading font-bold text-sm text-ink">{credits}</span>
                    </div>
                )}
            </div>

            {authLoading ? (
                <div className="font-heading font-bold text-ink/60">Loading…</div>
            ) : !isLoggedIn ? (
                <div data-testid="duel-login-gate" className="brutal-card p-8 bg-white max-w-lg">
                    <div className="w-12 h-12 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal mb-4">
                        <Lock className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                    <h2 className="font-heading font-black text-3xl text-ink tracking-tighter mb-2">Log in to fight</h2>
                    <p className="font-body text-ink/70 mb-5">Ante credits, get matched, take the pot.</p>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/login" className="brutal-btn-primary">Log in</Link>
                        <Link to="/register" className="brutal-btn-secondary">Create account</Link>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Arena */}
                    <div className="lg:col-span-8">
                        <div className="brutal-card p-3 bg-white">
                            <div className="flex items-center justify-between mb-2 px-1 text-sm font-heading font-bold">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-vibrantorange rounded-full border-2 border-ink" />
                                    <span data-testid="duel-my-hits">You · {myHits}</span>
                                </div>
                                <div className="font-heading font-black text-xl text-ink tabular-nums" data-testid="duel-timer">
                                    {Math.ceil(timeLeft)}s
                                </div>
                                <div className="flex items-center gap-2">
                                    <span data-testid="duel-opp-hits">{oppHits} · Opp</span>
                                    <div className="w-3 h-3 bg-[#2563EB] rounded-full border-2 border-ink" />
                                </div>
                            </div>
                            <canvas
                                ref={canvasRef}
                                data-testid="duel-canvas"
                                style={{ width: "100%", height: "auto", display: "block", cursor: phase === "playing" ? "crosshair" : "default", border: "3px solid #0A0A0A", borderRadius: 12 }}
                            />
                            <p className="mt-2 font-body text-[11px] text-ink/60 text-center">
                                WASD to move · Mouse to aim · Click to shoot
                            </p>
                        </div>
                    </div>

                    {/* Side panel */}
                    <div className="lg:col-span-4 space-y-4">
                        {phase === "idle" && (
                            <div data-testid="duel-lobby" className="brutal-card p-5 bg-vibrantyellow">
                                <div className="font-heading font-bold text-xs uppercase tracking-widest text-ink mb-3">Ante</div>
                                <div className="grid grid-cols-4 gap-2 mb-4">
                                    {ANTES.map((a) => (
                                        <button
                                            key={a}
                                            type="button"
                                            onClick={() => setAnte(a)}
                                            data-testid={`duel-ante-${a}`}
                                            className={`brutal-btn py-2 text-sm ${ante === a ? "bg-ink text-white" : "bg-white text-ink"}`}
                                        >
                                            {a}
                                        </button>
                                    ))}
                                </div>
                                <div className="brutal-card p-3 bg-white mb-3">
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Winner takes</span>
                                        <span className="font-heading font-black text-2xl text-ink">{ante * 2} cr</span>
                                    </div>
                                </div>
                                {error && (
                                    <p data-testid="duel-error" className="font-body font-semibold text-sm text-vibrantpink mb-2">{error}</p>
                                )}
                                <button onClick={findMatch} disabled={(credits ?? 0) < ante} className="brutal-btn-primary w-full" data-testid="duel-find-button">
                                    <Crosshair className="w-4 h-4 mr-2" strokeWidth={2.5} /> Find match
                                </button>
                                {(credits ?? 0) < ante && (
                                    <Link to="/buy-credits" className="brutal-btn-secondary w-full text-sm py-2 mt-2 block text-center">
                                        Buy credits
                                    </Link>
                                )}
                                <div className="mt-4 font-body text-[11px] text-ink/60 flex items-center gap-1.5">
                                    <Users className="w-3.5 h-3.5" strokeWidth={2.5} />
                                    {Object.entries(queueSizes).reduce((a, [k, v]) => a + v, 0)} waiting · ante {ante}: {queueSizes[ante] || 0}
                                </div>
                            </div>
                        )}

                        {phase === "queued" && (
                            <div data-testid="duel-queued" className="brutal-card p-6 bg-white text-center">
                                <Loader2 className="w-12 h-12 mx-auto text-vibrantorange animate-spin mb-3" strokeWidth={2.5} />
                                <h3 className="font-heading font-black text-2xl text-ink tracking-tighter">Looking for opponent…</h3>
                                <p className="font-body text-sm text-ink/60 mt-1">Ante {ante} cr · pot {ante * 2} cr</p>
                                <button onClick={cancelQueue} className="brutal-btn-white text-sm py-2 px-4 mt-4" data-testid="duel-cancel-queue">
                                    Cancel
                                </button>
                            </div>
                        )}

                        {phase === "playing" && match && (
                            <div data-testid="duel-hud" className="brutal-card p-4 bg-white">
                                <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Match</div>
                                <div className="font-heading font-black text-xl text-ink mt-1">
                                    @{match.you.username} <span className="text-ink/40">vs</span> @{match.opp.username}
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <div className="bg-vibrantorange text-white border-[3px] border-ink rounded-xl p-2 text-center">
                                        <div className="font-heading font-bold text-[10px] uppercase tracking-widest">You</div>
                                        <div className="font-heading font-black text-3xl tabular-nums">{myHits}</div>
                                    </div>
                                    <div className="bg-[#2563EB] text-white border-[3px] border-ink rounded-xl p-2 text-center">
                                        <div className="font-heading font-bold text-[10px] uppercase tracking-widest">Opp</div>
                                        <div className="font-heading font-black text-3xl tabular-nums">{oppHits}</div>
                                    </div>
                                </div>
                                <div className="mt-3 font-body text-xs text-ink/60">Pot: <b>{match.pool} cr</b></div>
                            </div>
                        )}

                        {phase === "ended" && (
                            <div data-testid="duel-result" className="brutal-card p-6 bg-white text-center">
                                {result ? (
                                    result.winner_id === match?.you?.id ? (
                                        <>
                                            <Trophy className="w-12 h-12 mx-auto text-vibrantorange mb-2" strokeWidth={2.5} />
                                            <h3 className="font-heading font-black text-3xl text-ink tracking-tighter">You win!</h3>
                                            <p className="font-body text-sm text-ink mt-1">+{result.pool} credits</p>
                                        </>
                                    ) : result.winner_id == null ? (
                                        <>
                                            <Coins className="w-12 h-12 mx-auto text-ink mb-2" strokeWidth={2.5} />
                                            <h3 className="font-heading font-black text-2xl text-ink tracking-tighter">Draw — refunded</h3>
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-12 h-12 mx-auto text-vibrantpink mb-2" strokeWidth={2.5} />
                                            <h3 className="font-heading font-black text-3xl text-ink tracking-tighter">You lost</h3>
                                            <p className="font-body text-sm text-ink/60 mt-1">{result.players.map(p => `@${p.username} ${p.hits}`).join(" · ")}</p>
                                            {result.walkover && <p className="font-body text-[11px] text-ink/40 mt-1">(opponent disconnected — pot kept by you if you won)</p>}
                                        </>
                                    )
                                ) : (
                                    <p className="font-body text-ink/60">Disconnected.</p>
                                )}
                                <button onClick={() => { setPhase("idle"); setResult(null); setMatch(null); setState(null); refresh && refresh(); }} className="brutal-btn-primary mt-4" data-testid="duel-play-again">
                                    Play again
                                </button>
                            </div>
                        )}

                        <PageChat room="duel" title="Duelists' chat" />
                    </div>
                </div>
            )}
        </div>
    );
}
